
import { useState, useRef, useEffect } from "react";
import CONFIG from "./config";
import { useTheme, useNotify, IS_DEMO, demoBlockToast } from "./App";
import { supabase, updateInventoryItem, uploadVendorLogo, setKioskPin, getKioskSlug,
  readTeamMembers, addTeamMember, removeTeamMember, updateTeamMemberRole, readAuditLog,
  linkSeatSubscription, linkDynamicPricingSubscription,
  saveDealBoardPhone, readLocations, addLocation, updateLocation, archiveLocation,
  getStaffLocationIds, setStaffLocations,
  PRICING_CATEGORIES, readCategoryPricingRules, saveCategoryPricingRule } from "./supabase";

async function checkImageMagicBytes(file) {
  const buf = await file.slice(0, 4).arrayBuffer();
  const bytes = new Uint8Array(buf);
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, "0")).join("");
  return (
    hex.startsWith("ffd8ff") ||          // JPEG
    hex.startsWith("89504e47") ||        // PNG
    hex.startsWith("424d")               // BMP
  );
}

const PROFANITY_LIST = [
  "fuck","shit","bitch","ass","damn","crap","piss","dick","cock","pussy",
  "cunt","bastard","slut","whore","fag","nigger","nigga","retard","douche",
  "asshole","bullshit","dumbass","jackass","motherfucker","goddamn",
];
function containsProfanity(text) {
  const lower = (text || "").toLowerCase();
  return PROFANITY_LIST.some(word => {
    const re = new RegExp(`\\b${word}\\b`, "i");
    return re.test(lower);
  });
}

const REQUIREMENT_MAX_LEN = 120;
function sanitizeRequirementInput(text) {
  return (text || "")
    .replace(/[^a-zA-Z0-9 .,'"!?$%()&\-]/g, "")
    .slice(0, REQUIREMENT_MAX_LEN);
}


function CategoryPricingSection({ S, T }) {
  const [locations, setLocations] = useState([]);
  const [locLoading, setLocLoading] = useState(true);
  const [activeLoc, setActiveLoc] = useState(null);
  const [rules, setRules]     = useState([]);
  const [active, setActive]   = useState(PRICING_CATEGORIES[0]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const { toast } = useNotify ? useNotify() : { toast: () => {} };

  useEffect(() => {
    readLocations()
      .then(locs => { setLocations(locs); if (locs.length) setActiveLoc(locs[0].ID); })
      .catch(e => toast("Couldn't load locations: " + e.message, "error"))
      .finally(() => setLocLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!activeLoc) return;
    setLoading(true);
    readCategoryPricingRules(activeLoc)
      .then(setRules)
      .catch(e => toast("Couldn't load category pricing: " + e.message, "error"))
      .finally(() => setLoading(false));
  }, [activeLoc]); // eslint-disable-line

  const rule = rules.find(r => r.Category === active);
  const setRule = (patch) => {
    setRules(rs => rs.map(r => r.Category === active ? { ...r, ...patch } : r));
  };

  const save = async () => {
    if (!rule || !activeLoc) return;
    const pct = parseFloat(rule.Percent);
    const dollar = parseFloat(rule.Dollar);
    const ceiling = parseFloat(rule.Ceiling);
    if (rule.Strategy === "top_plus_percent" || rule.Strategy === "top_minus_percent") {
      if (isNaN(pct) || pct < 0) { toast("Enter a valid percent for this rule", "error"); return; }
    }
    if (rule.Strategy === "top_plus_dollar" || rule.Strategy === "top_minus_dollar") {
      if (isNaN(dollar) || dollar < 0) { toast("Enter a valid dollar amount for this rule", "error"); return; }
    }
    if (isNaN(ceiling) || ceiling <= 0) { toast("Enter a valid dollar amount for the pricing ceiling", "error"); return; }
    setSaving(true);
    try {
      await saveCategoryPricingRule(activeLoc, rule);
      toast(`${active} pricing rule saved`, "success");
    } catch (e) {
      toast("Couldn't save: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>Category Pricing Rules</div>
      <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, marginBottom: 14, lineHeight: 1.6 }}>
        Rules are set per location, not company-wide — e.g. hold Sealed closer to market at
        your downtown store while discounting Raw Singles more at a location that needs to move volume.
      </div>

      {locLoading ? (
        <div style={{ fontSize: 13, color: T.textMuted }}>Loading locations…</div>
      ) : locations.length === 0 ? (
        <div style={{ fontSize: 13, color: T.textFaint }}>Add a location above before setting category pricing.</div>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>Location</label>
            <select style={S.select} value={activeLoc || ""} onChange={e => setActiveLoc(e.target.value)}>
              {locations.map(loc => (
                <option key={loc.ID} value={loc.ID}>{loc.StoreName} · #{loc.StoreNumber}</option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
            {PRICING_CATEGORIES.map(cat => (
              <button key={cat}
                style={{ ...S.chip(active === cat), padding: "7px 12px", fontSize: 12 }}
                onClick={() => setActive(cat)}>
                {cat}
              </button>
            ))}
          </div>

          {loading || !rule ? (
            <div style={{ fontSize: 13, color: T.textMuted }}>Loading…</div>
          ) : (
            <>
              <div style={{ ...S.infoBox, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Pricing Rule — {active}</div>
                <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 10, lineHeight: 1.6 }}>
                  Pick exactly one rule for how List Price gets calculated for this category at this location.
                </div>

                {[
                  { key: "top", label: "Price at the top of the Price Range" },
                  { key: "average", label: "Price at the average of the Price Range" },
                  { key: "top_plus_percent", label: "Price at the top of the Price Range plus" },
                  { key: "top_plus_dollar", label: "Price at the top of the Price Range plus" },
                  { key: "top_minus_percent", label: "Price below the top of the Price Range by" },
                  { key: "top_minus_dollar", label: "Price below the top of the Price Range by" },
                ].map(r => (
                  <div key={r.key} style={{
                    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                    padding: "8px 0", borderTop: r.key !== "top" ? `1px solid ${T.border || "rgba(255,255,255,0.08)"}` : "none",
                  }}>
                    <button
                      onClick={() => setRule({ Strategy: r.key })}
                      disabled={saving}
                      style={{
                        width: 40, height: 22, borderRadius: 11, border: "none", flexShrink: 0, position: "relative",
                        background: rule.Strategy === r.key ? "#3C66EE" : (T.surface2 || "#333"),
                      }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 8, background: "#fff", position: "absolute", top: 3,
                        left: rule.Strategy === r.key ? 21 : 3, transition: "left 0.15s ease",
                      }} />
                    </button>
                    <span style={{ fontSize: 12, color: T.textMuted, flex: "1 1 auto", minWidth: 160 }}>
                      {r.label}
                    </span>
                    {(r.key === "top_plus_percent" || r.key === "top_minus_percent") && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <input type="number" min="0" max="200" step="1"
                          value={rule.Percent} onChange={e => setRule({ Percent: e.target.value })}
                          style={{ ...S.input, width: 60, padding: "4px 6px", fontSize: 12, textAlign: "center" }} />
                        <span style={{ fontSize: 12, color: T.textMuted }}>%</span>
                      </div>
                    )}
                    {(r.key === "top_plus_dollar" || r.key === "top_minus_dollar") && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12, color: T.textMuted }}>$</span>
                        <input type="number" min="0" step="1"
                          value={rule.Dollar} onChange={e => setRule({ Dollar: e.target.value })}
                          style={{ ...S.input, width: 60, padding: "4px 6px", fontSize: 12, textAlign: "center" }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ ...S.infoBox, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Manual Review Ceiling — {active}</div>
                <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 8, lineHeight: 1.6 }}>
                  Items in this category above this reference price are flagged to price manually instead.
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: T.textMuted }}>Flag above $</span>
                  <input type="number" min="1" step="1"
                    value={rule.Ceiling} onChange={e => setRule({ Ceiling: e.target.value })}
                    style={{ ...S.input, width: 80, padding: "4px 6px", fontSize: 12, textAlign: "center" }} />
                </div>
              </div>

              <button style={S.btn("primary")} onClick={save} disabled={saving}>
                {saving ? "Saving…" : `Save ${active} Rule`}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

function StaffLocationPicker({ vendorUserId, S, T }) {
  const [open, setOpen]           = useState(false);
  const [locations, setLocations] = useState([]);
  const [assigned, setAssigned]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const { toast } = useNotify ? useNotify() : { toast: () => {} };

  const openPicker = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const [locs, ids] = await Promise.all([
        readLocations(),
        getStaffLocationIds(vendorUserId),
      ]);
      setLocations(locs);
      setAssigned(ids);
    } catch (e) {
      toast("Couldn't load locations: " + e.message, "error");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (locId) => {
    setAssigned(a => a.includes(locId) ? a.filter(id => id !== locId) : [...a, locId]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await setStaffLocations(vendorUserId, assigned);
      setOpen(false);
      toast("Locations updated", "success");
    } catch (e) {
      toast("Couldn't save: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px" }}
        onClick={openPicker}>
        📍 Locations
      </button>
      {open && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        }} onClick={() => !saving && setOpen(false)}>
          <div style={{ ...S.card, width: 320, maxWidth: "90vw" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Assign Locations</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 14, lineHeight: 1.5 }}>
              This person can only edit pricing at the locations checked below. Everything else about
              an item stays editable regardless of location.
            </div>
            {loading ? (
              <div style={{ fontSize: 13, color: T.textMuted }}>Loading…</div>
            ) : locations.length === 0 ? (
              <div style={{ fontSize: 13, color: T.textFaint }}>No locations set up yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                {locations.map(loc => (
                  <label key={loc.ID} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={assigned.includes(loc.ID)}
                      onChange={() => toggle(loc.ID)}
                      style={{ width: 16, height: 16, accentColor: "#3b82f6" }} />
                    <span style={{ fontSize: 13 }}>{loc.StoreName} <span style={{ color: T.textMuted }}>· #{loc.StoreNumber}</span></span>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button style={S.btn("primary")} onClick={save} disabled={saving || loading}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button style={S.btn("ghost")} onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function LockedFeatureCard({ title, description, onUpgradeClick, children }) {
  const { S, T } = useTheme();
  return (
    <div style={{ ...S.card, marginBottom: 16, position: "relative", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
        <span style={{ fontSize: 10, fontWeight: 700, color: T.primary, background: T.blueBg,
          padding: "2px 8px", borderRadius: T.radius.pill }}>PRO</span>
      </div>
      {description && (
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 14, lineHeight: 1.6 }}>{description}</div>
      )}
      <div style={{ opacity: 0.45, pointerEvents: "none", filter: "grayscale(30%)" }}>
        {children}
      </div>
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.borderSoft}` }}>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 8 }}>
          This is a Pro feature.
        </div>
        <button onClick={onUpgradeClick} style={{ ...S.btn("primary"), padding: "8px 16px", fontSize: 13 }}>
          Upgrade to Pro →
        </button>
      </div>
    </div>
  );
}

export default function Settings({ vendor, isPaid = true, isAdmin = false, isStaff = false, isEnterprise = false, canUse = ()=>true, onVendorUpdate, currentUserEmail, onUpgradeClick }) {
  const { S, T } = useTheme();
  const { toast, confirmAction } = useNotify();

  const [dynamicPricingSaving, setDynamicPricingSaving] = useState(false);
  const [dynamicPricingCeiling, setDynamicPricingCeiling] = useState(
    vendor?.dynamic_pricing_ceiling ?? 100
  );
  const [dynamicPricingPercent, setDynamicPricingPercent] = useState(
    vendor?.dynamic_pricing_percent != null ? String(vendor.dynamic_pricing_percent) : "10"
  );
  const [dynamicPricingDollar, setDynamicPricingDollar] = useState(
    vendor?.dynamic_pricing_dollar != null ? String(vendor.dynamic_pricing_dollar) : "5"
  );
  const [dynamicPricingStrategy, setDynamicPricingStrategy] = useState(
    vendor?.dynamic_pricing_strategy || "average"
  );
  const dynamicPricingEnabled = !!vendor?.dynamic_pricing_enabled;
  const hasDynamicPricing = isAdmin || isEnterprise || vendor?.dynamic_pricing_addon_status === "active";
  const [showDynamicPricingModal, setShowDynamicPricingModal] = useState(false);

  const [settingsTab, setSettingsTab] = useState("general");

  const [shopSaving, setShopSaving]           = useState(false);
  const [showLocation, setShowLocation]       = useState(!!vendor?.kiosk_show_location);
  const [storeMode, setStoreMode]             = useState(!!vendor?.store_mode_enabled);
  const [hidePrices, setHidePrices]           = useState(!!vendor?.kiosk_hide_prices);
  const [showRequestForm, setShowRequestForm] = useState(vendor?.kiosk_show_request_form !== false);
  const [notifyEmail, setNotifyEmail]         = useState(vendor?.kiosk_request_notify_email || "");
  const [locationOptions, setLocationOptions] = useState(
    Array.isArray(vendor?.kiosk_location_options) ? vendor.kiosk_location_options : []
  );
  const [newLocationValue, setNewLocationValue] = useState("");

  const saveShopSetting = async (patch) => {
    if (IS_DEMO) { demoBlockToast(toast); return; }
    setShopSaving(true);
    try {
      const { data, error } = await supabase.from("vendors").update(patch).eq("id", vendor.id).select().single();
      if (error) throw error;
      if (onVendorUpdate) onVendorUpdate(data);
      toast("Saved", "success");
    } catch (e) {
      toast("Failed to save: " + e.message, "error");
    }
    setShopSaving(false);
  };

  const addLocationOption = () => {
    const val = newLocationValue.trim();
    if (!val || locationOptions.includes(val)) { setNewLocationValue(""); return; }
    const next = [...locationOptions, val];
    setLocationOptions(next);
    setNewLocationValue("");
    saveShopSetting({ kiosk_location_options: next });
  };

  const removeLocationOption = (val) => {
    const next = locationOptions.filter(v => v !== val);
    setLocationOptions(next);
    saveShopSetting({ kiosk_location_options: next });
  };

  const [form, setForm] = useState({
    business_name:    vendor?.business_name    ?? "",
    trade_credit_rate: vendor?.trade_credit_rate != null ? (vendor.trade_credit_rate * 100).toFixed(0) : "80",
    cash_buy_rate:    vendor?.cash_buy_rate    != null ? (vendor.cash_buy_rate * 100).toFixed(0)    : "70",
    markup_rate:      vendor?.markup_rate      != null ? (vendor.markup_rate * 100).toFixed(0)      : "125",
    breakeven_buffer: vendor?.breakeven_buffer != null ? (vendor.breakeven_buffer * 100).toFixed(0) : "15",
    tax_tracking_enabled: !!vendor?.tax_tracking_enabled,
    tax_rate: vendor?.tax_rate != null ? String(vendor.tax_rate) : "6.25",
  });

  const [saving, setSaving] = useState(false);
  const [dealBoardPhone, setDealBoardPhone] = useState(vendor?.deal_board_phone || "");
  const [dealBoardSending, setDealBoardSending] = useState(false);
  const [dealBoardSavingAddress, setDealBoardSavingAddress] = useState(false);

  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState(null);

  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview]     = useState(vendor?.logo_url || null);
  const logoInputRef = useRef(null);

  const defaultConsign = {
    commission_rate: 2.5,
    min_card_value: 50,
    requirements: [
      "Card must be graded by PSA or Beckett (BGS)",
      "Estimated value of $50 or more",
    ],
  };
  const initialConsign = vendor?.consign_settings || defaultConsign;
  const [commissionRate, setCommissionRate] = useState(
    initialConsign.commission_rate != null ? String(initialConsign.commission_rate) : "2.5"
  );
  const [minCardValue, setMinCardValue] = useState(
    initialConsign.min_card_value != null ? String(initialConsign.min_card_value) : "50"
  );
  const [requirements, setRequirements] = useState(
    (initialConsign.requirements && initialConsign.requirements.length
      ? initialConsign.requirements
      : defaultConsign.requirements
    ).concat(Array(5).fill("")).slice(0, 5)
  );
  const [savingConsign, setSavingConsign] = useState(false);
  const [consignSaved, setConsignSaved]   = useState(false);

  const [teamMembers, setTeamMembers]   = useState([]);
  const [teamLoading, setTeamLoading]   = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole]   = useState("staff");
  const [teamError, setTeamError]       = useState("");
  const [teamSaving, setTeamSaving]     = useState(false);
  const [showSeatModal, setShowSeatModal] = useState(false);

  useEffect(() => {
    if (isPaid && vendor?.id) {
      setTeamLoading(true);
      readTeamMembers(vendor.id)
        .then(members => { setTeamMembers(members); setTeamLoading(false); })
        .catch(() => setTeamLoading(false));
    }
  }, [vendor?.id, isPaid]);

  const FREE_TEAM_MEMBERS = 1;
  const activeSeatCount = vendor?.team_seat_status === "active" ? (vendor?.team_seat_quantity || 0) : 0;
  const seatLimitReached = teamMembers.length >= FREE_TEAM_MEMBERS + activeSeatCount;

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (IS_DEMO) { demoBlockToast(toast); return; }

    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/bmp"];
    if (!allowed.includes(file.type)) {
      toast(`Unsupported file type: ${file.type || "unknown"}. Please use JPG, BMP, or PNG.`, "alert");
      return;
    }

    const isRealImage = await checkImageMagicBytes(file);
    if (!isRealImage) {
      toast("This file doesn't look like a valid image. Please choose a different file.", "alert");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      toast(`Logo is ${sizeMB}MB — must be under 2MB. Try compressing the image first.`, "alert");
      return;
    }

    setLogoUploading(true);
    try {
      const url = await uploadVendorLogo(file, vendor.id);
      setLogoPreview(url);
      if (onVendorUpdate) onVendorUpdate({ ...vendor, logo_url: url });
      toast("Logo updated — refresh your kiosk to see the change", "success");
    } catch(e) {
      toast("Logo upload failed: " + e.message, "error");
    }
    setLogoUploading(false);
  };

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  const [kioskSlug, setKioskSlug]       = useState(null);
  const [kioskPin, setKioskPinVal]      = useState("");
  const [showPin, setShowPin]           = useState(false);
  const [savingPin, setSavingPin]       = useState(false);
  const [pinSaved, setPinSaved]         = useState(false);
  const [hasPIN, setHasPIN]             = useState(false);

  useEffect(() => {
    getKioskSlug().then(d => {
      if (d?.kiosk_slug) setKioskSlug(d.kiosk_slug);
    }).catch(() => {});
    if (vendor?.id) {
      supabase.from("vendors").select("kiosk_pin, kiosk_slug")
        .eq("id", vendor.id).single()
        .then(({ data }) => {
          if (data?.kiosk_pin) setHasPIN(true);
          if (data?.kiosk_slug) setKioskSlug(data.kiosk_slug);
        });
    }
  }, [vendor?.id]);

  const displaySlug = IS_DEMO ? "vendor-demo" : kioskSlug;

  const kioskUrl = displaySlug
    ? `https://app.vendorstax.com/kiosk.html?v=${displaySlug}`
    : null;

  const shopUrl = displaySlug
    ? `https://app.vendorstax.com/shop.html?s=${displaySlug}`
    : null;

  const storeUrl = displaySlug
    ? `https://app.vendorstax.com/store.html?v=${displaySlug}`
    : null;

  const referralLink = vendor?.email
    ? `https://app.vendorstax.com/?ref=${encodeURIComponent(vendor.email)}`
    : null;

  const handleSavePin = async () => {
    if (IS_DEMO) { demoBlockToast(toast); return; }
    if (!/^\d{5}$/.test(kioskPin)) {
      toast("PIN must be exactly 5 digits", "alert"); return;
    }
    setSavingPin(true);
    try {
      await setKioskPin(vendor.id, kioskPin);
      setHasPIN(true);
      setPinSaved(true);
      setKioskPinVal("");
      setShowPin(false);
      toast("Kiosk PIN saved", "success");
      setTimeout(() => setPinSaved(false), 3000);
    } catch(e) { toast("Failed to save PIN: " + e.message, "error"); }
    setSavingPin(false);
  };

  const handleRequirementChange = (index, value) => {
    const cleaned = sanitizeRequirementInput(value);
    setRequirements(prev => {
      const next = [...prev];
      next[index] = cleaned;
      return next;
    });
  };

  const handleSaveConsignSettings = async () => {
    if (IS_DEMO) { demoBlockToast(toast); return; }
    const rate = parseFloat(commissionRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast("Commission rate must be a number between 0 and 100.", "alert");
      return;
    }

    const cleanedRequirements = requirements
      .map(r => r.trim())
      .filter(r => r.length > 0);

    for (const req of cleanedRequirements) {
      if (containsProfanity(req)) {
        toast(`That requirement contains language that isn't allowed: "${req.slice(0,40)}..."`, "alert");
        return;
      }
    }

    if (cleanedRequirements.some(r => r.length > REQUIREMENT_MAX_LEN)) {
      toast(`Each requirement must be ${REQUIREMENT_MAX_LEN} characters or fewer.`, "alert");
      return;
    }

    setSavingConsign(true);
    try {
      const minVal = parseFloat(minCardValue) || 0;
      const consign_settings = {
        commission_rate: rate,
        min_card_value: minVal,
        requirements: cleanedRequirements,
      };
      const { data, error } = await supabase
        .from("vendors")
        .update({ consign_settings })
        .eq("id", vendor.id)
        .select()
        .single();
      if (error) throw error;

      onVendorUpdate({ ...vendor, ...data });
      setConsignSaved(true);
      toast("Kiosk consignment requirements saved", "success");
      setTimeout(() => setConsignSaved(false), 3000);
    } catch(e) {
      toast("Failed to save: " + e.message, "error");
    }
    setSavingConsign(false);
  };

  const STRATEGY_LABELS = {
    top: "the top of the Price Range",
    average: "the average of the Price Range",
    top_plus_percent: "the top of the Price Range plus your chosen %",
    top_plus_dollar: "the top of the Price Range plus your chosen flat $ amount",
    top_minus_percent: "your chosen % below the top of the Price Range",
    top_minus_dollar: "your chosen flat $ amount below the top of the Price Range",
  };

  const handleTogglePricing = async () => {
    if (IS_DEMO) { demoBlockToast(toast); return; }
    const turningOn = !dynamicPricingEnabled;
    if (turningOn) {
      const ok = await confirmAction(
        "You're turning on dynamic pricing. From now on, your List Price for In Stock cards will be " +
        `set automatically to ${STRATEGY_LABELS[dynamicPricingStrategy] || "the average of the Price Range"} ` +
        `each time you refresh prices, as long as the Reference Price is $${dynamicPricingCeiling || 100} or under. ` +
        `Cards above that are flagged for you to price manually instead. Prices never go below your cost + markup. ` +
        "We always recommend reviewing pricing afterward to make sure it meets your criteria.",
        { confirmLabel: "Turn On Price Setting" }
      );
      if (!ok) return;
    } else {
      const ok = await confirmAction("Turn off Price Setting? List Price will no longer update automatically.");
      if (!ok) return;
    }

    setDynamicPricingSaving(true);
    try {
      const pct = parseFloat(dynamicPricingPercent);
      const dollar = parseFloat(dynamicPricingDollar);
      const { data, error } = await supabase
        .from("vendors")
        .update({
          dynamic_pricing_enabled: turningOn,
          dynamic_pricing_strategy: dynamicPricingStrategy || "average",
          dynamic_pricing_percent: !isNaN(pct) ? pct : 10,
          dynamic_pricing_dollar: !isNaN(dollar) ? dollar : 5,
        })
        .eq("id", vendor.id)
        .select()
        .single();
      if (error) throw error;
      onVendorUpdate({ ...vendor, ...data });
      toast(turningOn ? "Price Setting turned on" : "Price Setting turned off", "success");
    } catch (e) {
      toast("Failed to update Price Setting: " + e.message, "error");
    }
    setDynamicPricingSaving(false);
  };


  const handleSave = async () => {
    if (IS_DEMO) { demoBlockToast(toast); return; }
    setSaving(true); setError(null);
    try {
      const pct = parseFloat(dynamicPricingPercent);
      const dollar = parseFloat(dynamicPricingDollar);
      const ceiling = parseFloat(dynamicPricingCeiling);
      if (dynamicPricingStrategy === "top_plus_percent" && (isNaN(pct) || pct < 0)) {
        toast("Enter a valid percentage for the pricing rule", "error");
        setSaving(false);
        return;
      }
      if (dynamicPricingStrategy === "top_plus_dollar" && (isNaN(dollar) || dollar < 0)) {
        toast("Enter a valid dollar amount for the pricing rule", "error");
        setSaving(false);
        return;
      }
      if (isNaN(ceiling) || ceiling <= 0) {
        toast("Enter a valid dollar amount for the pricing ceiling", "error");
        setSaving(false);
        return;
      }

      const newBusinessName = form.business_name.trim() || null;

      if (newBusinessName && newBusinessName.toLowerCase().replace(/[^a-z0-9]/g, "") === "vendordemo") {
        toast("This business name isn't available. Please choose another.", "error");
        setSaving(false);
        return;
      }

      let newSlug = kioskSlug;
      if (newBusinessName && newBusinessName !== vendor?.business_name) {
        newSlug = newBusinessName
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")   // strip special chars
          .replace(/\s+/g, "-")             // spaces to hyphens
          .replace(/-+/g, "-")              // collapse multiple hyphens
          .slice(0, 40)                     // max 40 chars
          .replace(/^-|-$/g, "");           // trim leading/trailing hyphens

        if (newSlug === "vendor-demo") {
          newSlug = `vendor-demo-${vendor.id.slice(0,4)}`;
        }

        const { data: existing } = await supabase
          .from("vendors")
          .select("id")
          .eq("kiosk_slug", newSlug)
          .neq("id", vendor.id)
          .maybeSingle();
        if (existing) newSlug = `${newSlug}-${vendor.id.slice(0,6)}`;
      }

      const updates = {
        business_name:    newBusinessName,
        kiosk_slug:       newSlug,
        trade_credit_rate: parseFloat(form.trade_credit_rate) / 100,
        cash_buy_rate:    parseFloat(form.cash_buy_rate)    / 100,
        markup_rate:      parseFloat(form.markup_rate)      / 100,
        breakeven_buffer: parseFloat(form.breakeven_buffer) / 100,
        tax_tracking_enabled: !!form.tax_tracking_enabled,
        tax_rate: parseFloat(form.tax_rate) || 0,
        dynamic_pricing_strategy: dynamicPricingStrategy,
        dynamic_pricing_percent: !isNaN(pct) ? pct : 10,
        dynamic_pricing_dollar: !isNaN(dollar) ? dollar : 5,
        dynamic_pricing_ceiling: ceiling,
      };

      const { data, error } = await supabase
        .from("vendors")
        .update(updates)
        .eq("id", vendor.id)
        .select()
        .single();
      if (error) throw error;

      if (newSlug !== kioskSlug) setKioskSlug(newSlug);

      onVendorUpdate({ ...vendor, ...data });
      setSaved(true);
      toast("Settings saved", "success");
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const tradePct    = parseFloat(form.trade_credit_rate) || 80;
  const cashPct     = parseFloat(form.cash_buy_rate)     || 70;
  const markupPct   = parseFloat(form.markup_rate)       || 125;
  const breakevenPct= parseFloat(form.breakeven_buffer)  || 15;

  const exampleMV   = 100;
  const tradeCredit = (exampleMV * tradePct / 100).toFixed(2);
  const cashOffer   = (exampleMV * cashPct  / 100).toFixed(2);
  const minSell     = Math.ceil(exampleMV * (markupPct / 100));
  const breakeven   = Math.ceil(exampleMV * (1 + breakevenPct / 100));

  const SECTION = (title, subtitle) => (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );

  const TIP = ({ children }) => (
    <div style={{ fontSize: 12, color: T.textMuted, background: T.surface2,
      borderRadius: 8, padding: "10px 12px", marginTop: 6, lineHeight: 1.6,
      borderLeft: `3px solid #3b82f6` }}>
      💡 {children}
    </div>
  );

  const Field = ({ label, value, onChange, min, max, unit = "%", children }) => (
    <div style={{ marginBottom: 16 }}>
      <label style={S.label}>{label}</label>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input
          type="range" min={min} max={max} step="1"
          value={value} onChange={e => onChange(e.target.value)}
          style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 4,
          background: T.surface2, border: `1px solid ${T.border}`,
          borderRadius: 8, padding: "6px 10px", minWidth: 64 }}>
          <input
            type="number" min={min} max={max} value={value}
            onChange={e => onChange(e.target.value)}
            style={{ width: 40, background: "transparent", border: "none",
              color: T.text, fontSize: 15, fontWeight: 700,
              fontFamily: "'DM Mono',monospace", outline: "none",
              textAlign: "right" }} />
          <span style={{ fontSize: 13, color: T.textMuted }}>{unit}</span>
        </div>
      </div>
      {children}
    </div>
  );

  if (isStaff) {
    return (
      <div style={S.page}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>⚙️ Settings</div>
        <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 24 }}>
          Ask an admin if you need anything changed here.
        </div>

        {kioskUrl && (
          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>📱 Kiosk URL</div>
            <div style={{ display:"flex", gap:8, alignItems:"center", marginTop: 10 }}>
              <div style={{
                ...S.input, flex:1, fontSize:11, color:T.textMuted,
                fontFamily:"'DM Mono',monospace", overflow:"hidden",
                textOverflow:"ellipsis", whiteSpace:"nowrap",
                pointerEvents:"none", userSelect:"all",
              }}>
                {kioskUrl}
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(kioskUrl); toast("Kiosk URL copied", "success"); }}
                style={{ ...S.btn("ghost"), padding:"8px 14px", fontSize:12, flexShrink:0 }}>
                Copy
              </button>
            </div>
          </div>
        )}

        {shopUrl && (
          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>🌐 Online Shop URL</div>
            <div style={{ display:"flex", gap:8, alignItems:"center", marginTop: 10 }}>
              <div style={{
                ...S.input, flex:1, fontSize:11, color:T.textMuted,
                fontFamily:"'DM Mono',monospace", overflow:"hidden",
                textOverflow:"ellipsis", whiteSpace:"nowrap",
                pointerEvents:"none",
              }}>
                {shopUrl}
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(shopUrl); toast("Shop URL copied", "success"); }}
                style={{ ...S.btn("ghost"), padding:"8px 14px", fontSize:12, flexShrink:0 }}>
                Copy
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={S.page}>

      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>⚙️ Settings</div>
      <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 16 }}>
        Configure your business rules. These rates affect how the app calculates
        offers, minimum sell prices, and trade credit across all features.
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:20, borderBottom:`1px solid ${T.border||"rgba(255,255,255,0.1)"}`, paddingBottom:0 }}>
        {[["general","General"],["cardshop","For Card Shops"]].map(([key,label])=>(
          <button key={key} onClick={()=>setSettingsTab(key)}
            style={{
              padding:"10px 4px", marginRight:16, background:"none", border:"none",
              borderBottom:`2px solid ${settingsTab===key ? "#3C66EE" : "transparent"}`,
              color: settingsTab===key ? (T.text||"#fff") : T.textMuted,
              fontSize:14, fontWeight: settingsTab===key ? 700 : 500, cursor:"pointer",
            }}>
            {label}
          </button>
        ))}
      </div>

      {settingsTab === "general" && (<>

      <div style={{ ...S.card, marginBottom: 16 }}>
        {SECTION("Business Info")}
        <div style={{ marginTop: 12 }}>
          <label style={S.label}>Business Name</label>
          <input style={{ ...S.input, fontSize: 15 }}
            value={form.business_name}
            onChange={e => set("business_name", e.target.value)}
            placeholder="e.g. Parker Cards & Collectibles" />
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>
            Appears on your Kiosk and receipts printed from Show Mode.
          </div>
          <div style={{ fontSize: 12, color: "#fbbf24", marginTop: 4, lineHeight: 1.5 }}>
            ⚠️ Changing your business name will also update your online store URL.
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <label style={S.label}>Kiosk Logo</label>
          <div style={{ display:"flex", alignItems:"center", gap:16, marginTop:8 }}>
            <div style={{
              width:80, height:80, borderRadius:12, overflow:"hidden",
              background:T.surface, border:`1px solid ${T.border||"rgba(255,255,255,0.1)"}`,
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
            }}>
              {logoPreview ? (
                <img src={logoPreview} alt="Kiosk logo"
                  style={{width:"100%", height:"100%", objectFit:"contain"}} />
              ) : (
                <span style={{fontSize:28}}>🏪</span>
              )}
            </div>
            <div style={{flex:1}}>
              <input ref={logoInputRef} type="file" accept="image/*"
                style={{display:"none"}} onChange={handleLogoSelect} />
              <button
                style={{...S.btn("primary"), marginBottom:6}}
                onClick={()=>logoInputRef.current?.click()}
                disabled={logoUploading}>
                {logoUploading ? "Uploading…" : logoPreview ? "Change Logo" : "Upload Logo"}
              </button>
              <div style={{fontSize:11, color:T.textFaint}}>
                PNG, JPG, SVG or WebP · Max 2MB · Displays on your Kiosk screen
              </div>
            </div>
          </div>
        </div>
      </div>

      {referralLink && (
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>🎁 Refer a Vendor</div>
        <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4, marginBottom: 10 }}>
          Invite 2 vendors to VendorStax and get an extra 5 days added to your Pro trial automatically, once they sign up.
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center", marginTop: 10 }}>
          <div style={{
            ...S.input, flex:1, fontSize:11, color:T.textMuted,
            fontFamily:"'DM Mono',monospace", overflow:"hidden",
            textOverflow:"ellipsis", whiteSpace:"nowrap",
            pointerEvents:"none", userSelect:"all",
          }}>
            {referralLink}
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(referralLink); toast("Referral link copied", "success"); }}
            style={{ ...S.btn("ghost"), padding:"8px 14px", fontSize:12, flexShrink:0 }}>
            Copy
          </button>
        </div>
      </div>
      )}

      {isPaid ? (
      <div style={{ ...S.card, marginBottom: 16 }}>
        {SECTION("Trade Credit Rate",
          "What % of a card's market value you offer as store credit in a trade.")}
        <div style={{ marginTop: 12 }}>
          <Field label="Trade credit offered" value={form.trade_credit_rate}
            onChange={v => set("trade_credit_rate", v)} min={50} max={100}>
            <TIP>
              Most vendors offer <strong>75–85%</strong> trade credit. At 80%, a customer
              trading in a $100 card receives $80 in store credit. Higher rates attract
              more trades but reduce your margin. Lower rates protect margin but may
              lose customers to competitors.
            </TIP>
          </Field>

          <div style={{ ...S.infoBox, marginTop: 8 }}>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>
              Example — customer trades in a card with $100 market value:
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span style={{ color: T.textMuted }}>Store credit offered</span>
              <span style={{ fontWeight: 700, color: "#4ade80" }}>${tradeCredit}</span>
            </div>
          </div>
        </div>
      </div>
      ) : (
        <LockedFeatureCard title="Trade Credit Rate" onUpgradeClick={onUpgradeClick}
          description="What % of a card's market value you offer as store credit in a trade.">
          <Field label="Trade credit offered" value={form.trade_credit_rate} onChange={()=>{}} min={50} max={100} />
        </LockedFeatureCard>
      )}

      {isPaid ? (
      <div style={{ ...S.card, marginBottom: 16 }}>
        {SECTION("Cash Buy Rate",
          "What % of market value you offer when buying a card outright for cash.")}
        <div style={{ marginTop: 12 }}>
          <Field label="Cash buy offered" value={form.cash_buy_rate}
            onChange={v => set("cash_buy_rate", v)} min={40} max={90}>
            <TIP>
              Cash buys are typically <strong>60–75%</strong> of market value — lower than
              trade credit because you're paying out real money. Most vendors set cash
              rate 10% below their trade rate. At 65%, you're buying CGC slabs at a
              discount that leaves room for eBay fees and profit.
            </TIP>
          </Field>

          <div style={{ ...S.infoBox, marginTop: 8 }}>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>
              Example — buying a card with $100 market value for cash:
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span style={{ color: T.textMuted }}>Cash offer to customer</span>
              <span style={{ fontWeight: 700, color: "#f87171" }}>${cashOffer}</span>
            </div>
          </div>
        </div>
      </div>
      ) : (
        <LockedFeatureCard title="Cash Buy Rate" onUpgradeClick={onUpgradeClick}
          description="What % of market value you offer when buying a card outright for cash.">
          <Field label="Cash buy offered" value={form.cash_buy_rate} onChange={()=>{}} min={40} max={90} />
        </LockedFeatureCard>
      )}

      {isPaid ? (
      <div style={{ ...S.card, marginBottom: 16 }}>
        {SECTION("Minimum Sell Price Markup",
          "The multiplier applied to your acquisition cost to calculate the minimum price you should sell a card for.")}
        <div style={{ marginTop: 12 }}>
          <Field label="Markup multiplier" value={form.markup_rate}
            onChange={v => set("markup_rate", v)} min={100} max={300} unit="%">
            <TIP>
              At <strong>125%</strong>, a card that cost you $20 has a minimum sell price
              of $25 — covering your cost plus a 25% floor margin. This is the
              lowest you should go before you're selling at a loss. Your actual list
              price should be higher; this is just your floor. Most vendors use
              115–150%.
            </TIP>
          </Field>

          <div style={{ ...S.infoBox, marginTop: 8 }}>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>
              Example — card that cost you ${exampleMV}:
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span style={{ color: T.textMuted }}>Minimum sell price</span>
              <span style={{ fontWeight: 700, color: "#88bdf2" }}>${minSell}</span>
            </div>
          </div>
        </div>
      </div>
      ) : (
        <LockedFeatureCard title="Minimum Sell Price Markup" onUpgradeClick={onUpgradeClick}
          description="The multiplier applied to your acquisition cost to calculate the minimum price you should sell a card for.">
          <Field label="Markup multiplier" value={form.markup_rate} onChange={()=>{}} min={100} max={300} unit="%" />
        </LockedFeatureCard>
      )}

      {isPaid ? (
      <div style={{ ...S.card, marginBottom: 24 }}>
        {SECTION("Breakeven Buffer",
          "An additional % added on top of cost to account for fees, shipping, and platform costs when calculating your true breakeven.")}
        <div style={{ marginTop: 12 }}>
          <Field label="Buffer above cost" value={form.breakeven_buffer}
            onChange={v => set("breakeven_buffer", v)} min={0} max={40}>
            <TIP>
              eBay takes roughly <strong>13–16%</strong> in fees. If you plan to sell
              cards on eBay, set this to at least 15% so the app accounts for fees
              when showing your true breakeven price. For show-only sales you can
              set this lower (5–8%) to cover just table costs.
            </TIP>
          </Field>

          <div style={{ ...S.infoBox, marginTop: 8 }}>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>
              Example — card that cost you ${exampleMV}, true breakeven including fees:
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span style={{ color: T.textMuted }}>Breakeven sell price</span>
              <span style={{ fontWeight: 700, color: "#fbbf24" }}>${breakeven}</span>
            </div>
          </div>
        </div>
      </div>
      ) : (
        <LockedFeatureCard title="Breakeven Buffer" onUpgradeClick={onUpgradeClick}
          description="An additional % added on top of cost to account for fees, shipping, and platform costs when calculating your true breakeven.">
          <Field label="Buffer above cost" value={form.breakeven_buffer} onChange={()=>{}} min={0} max={40} />
        </LockedFeatureCard>
      )}

      {isPaid ? (
      <div style={{ ...S.card, marginBottom: 24 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Tax Tracking</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, lineHeight:1.5 }}>
              Collect sales tax on top of your sale price. Applies to Manual Sales,
              Show Mode, and Store Mode — not Trades (no cash changes hands) and not
              the Online Shop (no checkout there yet).
            </div>
          </div>
          <button
            onClick={()=>set("tax_tracking_enabled", !form.tax_tracking_enabled)}
            style={{
              width:44, height:26, borderRadius:13, border:"none", flexShrink:0, marginLeft:12,
              background: form.tax_tracking_enabled ? "#3C66EE" : (T.surface2||"#333"), position:"relative", cursor:"pointer",
            }}>
            <div style={{
              width:20, height:20, borderRadius:10, background:"#fff", position:"absolute", top:3,
              left: form.tax_tracking_enabled ? 21 : 3, transition:"left 0.15s ease",
            }} />
          </button>
        </div>

        {form.tax_tracking_enabled && (
          <div style={{ marginTop:16, paddingTop:16, borderTop:`1px solid ${T.borderSoft||T.border}` }}>
            <label style={S.label}>Default Tax Rate (%)</label>
            <input style={{...S.input, maxWidth:140}} type="number" min="0" max="20" step="0.01"
              value={form.tax_rate} onChange={e=>set("tax_rate", e.target.value)} placeholder="6.25" />

            <div style={{ ...S.infoBox, marginTop:14 }}>
              <div style={{ fontSize:12, color:T.textMuted, marginBottom:4 }}>
                Tax Calculation Method
              </div>
              <div style={{ fontSize:13, fontWeight:600 }}>
                Tax added to sale
              </div>
              <div style={{ fontSize:11, color:T.textFaint, marginTop:4 }}>
                Tax is calculated on top of your List Price at the moment a sale completes,
                not backed out of it. The rate used is snapshotted onto each sale, so
                changing this rate later never changes past sales.
              </div>
            </div>
          </div>
        )}
      </div>
      ) : (
        <LockedFeatureCard title="Tax Tracking" onUpgradeClick={onUpgradeClick}
          description="Collect sales tax on top of your sale price across Manual Sales, Show Mode, and Store Mode.">
          <div style={{ fontSize:13, color:T.textMuted }}>Default rate 6.25% · Tax added to sale</div>
        </LockedFeatureCard>
      )}

      {isPaid ? (
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>🖥 Kiosk Mode</div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
          Share your unique kiosk link with your display device at shows.
          Customers can browse your available inventory without accessing your account.
        </div>

        {kioskUrl ? (
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Your Kiosk URL</label>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <div style={{
                ...S.input, flex:1, fontSize:11, color:T.textMuted,
                fontFamily:"'DM Mono',monospace", overflow:"hidden",
                textOverflow:"ellipsis", whiteSpace:"nowrap",
                pointerEvents:"none", userSelect:"all",
              }}>
                {kioskUrl}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(kioskUrl);
                  toast("Kiosk URL copied", "success");
                }}
                style={{ ...S.btn("ghost"), padding:"8px 14px", fontSize:12, flexShrink:0 }}>
                Copy
              </button>
            </div>
            <div style={{ fontSize:11, color:T.textFaint, marginTop:6 }}>
              Open this on a tablet or display device at your show table.
            </div>
          </div>
        ) : (
          <div style={{ ...S.infoBox, marginBottom:16 }}>
            <div style={{ fontSize:12, color:T.textMuted }}>
              Set a Business Name above to generate your kiosk URL.
            </div>
          </div>
        )}

        <div>
          <label style={S.label}>
            Kiosk PIN
            <span style={{ fontWeight:400, color:T.textFaint, marginLeft:6 }}>
              {hasPIN ? "✓ PIN is set" : "Not set — kiosk is unprotected"}
            </span>
          </label>
          <div style={{ fontSize:12, color:T.textMuted, marginBottom:8, lineHeight:1.5 }}>
            {hasPIN
              ? "Enter a new 5-digit PIN to reset it. The old PIN will stop working immediately."
              : "Set a 5-digit PIN to protect your kiosk. This pin is used for Show Mode and Store Mode. Anyone with your kiosk URL will need this PIN to access it."}
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
            <div style={{ position:"relative", flex:1 }}>
              <input
                style={{ ...S.input, fontFamily:"'DM Mono',monospace",
                  fontSize:20, letterSpacing:8, textAlign:"center",
                  paddingRight:40 }}
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                maxLength={5}
                placeholder="•••••"
                value={kioskPin}
                onChange={e => setKioskPinVal(e.target.value.replace(/\D/g,"").slice(0,5))}
              />
              <button
                onClick={() => setShowPin(p => !p)}
                style={{
                  position:"absolute", right:10, top:"50%",
                  transform:"translateY(-50%)",
                  background:"none", border:"none",
                  color:T.textFaint, cursor:"pointer", fontSize:16, padding:4,
                }}>
                {showPin ? "🙈" : "👁"}
              </button>
            </div>
            <button
              style={{ ...S.btn("primary"), padding:"10px 18px", flexShrink:0 }}
              onClick={handleSavePin}
              disabled={savingPin || kioskPin.length !== 5}>
              {savingPin ? "Saving…" : pinSaved ? "✓ Saved" : hasPIN ? "Reset PIN" : "Set PIN"}
            </button>
          </div>
          {kioskPin.length > 0 && kioskPin.length < 5 && (
            <div style={{ fontSize:11, color:"#FF9600" }}>
              {5 - kioskPin.length} more digit{5 - kioskPin.length !== 1 ? "s" : ""} needed
            </div>
          )}
        </div>
      </div>
      ) : (
        <LockedFeatureCard title="🖥 Kiosk Mode" onUpgradeClick={onUpgradeClick}
          description="Share a PIN-protected kiosk link with a display device at shows, so customers can browse your inventory without touching your account.">
          <div style={{ fontSize:13, color:T.textMuted }}>yourshop.vendorstax.com/kiosk/••••• (PIN protected)</div>
        </LockedFeatureCard>
      )}

      {shopUrl && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>🌐 Online Shop</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12, lineHeight: 1.6 }}>
            Share this link so customers can browse your online inventory and claim cards.
            Cards must be marked 🌐 Online in their details to appear here.
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <div style={{
              ...S.input, flex:1, fontSize:11, color:T.textMuted,
              fontFamily:"'DM Mono',monospace", overflow:"hidden",
              textOverflow:"ellipsis", whiteSpace:"nowrap",
              pointerEvents:"none",
            }}>
              {shopUrl}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(shopUrl);
                toast("Shop URL copied", "success");
              }}
              style={{ ...S.btn("ghost"), padding:"8px 14px", fontSize:12, flexShrink:0 }}>
              Copy
            </button>
          </div>
          <div style={{ fontSize:11, color:T.textFaint, marginTop:6 }}>
            Share on social media, your website, or anywhere customers shop online.
          </div>
          {!isPaid && (
            <div style={{
              marginTop:12, padding:"10px 12px",
              background:"rgba(251,191,36,0.08)", border:"0.5px solid rgba(251,191,36,0.3)",
              borderRadius:8,
            }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:3 }}>
                ⭐ Free plan — 20 card limit
              </div>
              <div style={{ fontSize:12, color:T.textMuted, lineHeight:1.5 }}>
                Your shop displays up to 20 cards. Upgrade to Pro to list your full inventory.
              </div>
            </div>
          )}
        </div>
      )}

      {isPaid ? (
      <div style={{ ...S.card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
          💎 Consignment Settings
        </div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
          Set your commission rate, minimum card value, and the requirements that appear
          on your kiosk consignment form.
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={S.label}>Commission Rate (%)</label>
          <div style={{ fontSize:11, color:T.textFaint, marginBottom:8 }}>
            Your cut of the sale price. Shown to customers on the kiosk form and
            used to calculate your profit in the card drawer.
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8, maxWidth:160 }}>
            <input
              style={{ ...S.input, textAlign:"center" }}
              type="number" min="0" max="100" step="0.1"
              value={commissionRate}
              onChange={e => setCommissionRate(e.target.value)}
              placeholder="2.5"
            />
            <span style={{ fontSize:14, color:T.textMuted, fontWeight:600 }}>%</span>
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={S.label}>Minimum Card Value ($)</label>
          <div style={{ fontSize:11, color:T.textFaint, marginBottom:8 }}>
            Cards below this value cannot be marked as consignment.
            Replaces the hardcoded $500 minimum.
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8, maxWidth:160 }}>
            <span style={{ fontSize:14, color:T.textMuted, fontWeight:600 }}>$</span>
            <input
              style={{ ...S.input, textAlign:"center" }}
              type="number" min="0" step="1"
              value={minCardValue}
              onChange={e => setMinCardValue(e.target.value)}
              placeholder="50"
            />
          </div>
        </div>

        <div>
          <label style={S.label}>Your Requirements (up to 5)</label>
          <div style={{ fontSize:11, color:T.textFaint, marginBottom:10 }}>
            These appear as a bulleted list on the consignment form. Letters, numbers,
            and basic punctuation only — up to {REQUIREMENT_MAX_LEN} characters each.
          </div>
          {requirements.map((req, i) => (
            <div key={i} style={{ marginBottom:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:12, color:T.textFaint, width:18, flexShrink:0 }}>
                  {i+1}.
                </span>
                <input
                  style={{ ...S.input, flex:1 }}
                  value={req}
                  onChange={e => handleRequirementChange(i, e.target.value)}
                  placeholder={i === 0
                    ? "e.g. Card must be graded by PSA or Beckett"
                    : i === 1
                    ? "e.g. Estimated value of $500 or more"
                    : "Optional"}
                  maxLength={REQUIREMENT_MAX_LEN}
                />
              </div>
              <div style={{
                fontSize:10, color: req.length > REQUIREMENT_MAX_LEN - 15 ? "#FF9600" : T.textFaint,
                textAlign:"right", marginTop:2, marginLeft:26,
              }}>
                {req.length}/{REQUIREMENT_MAX_LEN}
              </div>
            </div>
          ))}
        </div>

        <button
          style={{ ...S.btn("primary"), width:"100%", marginTop:6 }}
          onClick={handleSaveConsignSettings}
          disabled={savingConsign}>
          {savingConsign ? "Saving…" : consignSaved ? "✓ Saved" : "Save Consignment Requirements"}
        </button>
      </div>
      ) : (
        <LockedFeatureCard title="💎 Consignment Settings" onUpgradeClick={onUpgradeClick}
          description="Set your commission rate, minimum card value, and the requirements that appear on your kiosk consignment form.">
          <div style={{ fontSize:13, color:T.textMuted }}>Commission: 20% · Min card value: $10</div>
        </LockedFeatureCard>
      )}

      {isPaid ? (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>👤 Team Members</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
            Add staff or additional admins to your account. Each person logs in with their own email.
            One email can only belong to one VendorStax account.
          </div>

          {teamLoading ? (
            <div style={{ fontSize: 13, color: T.textMuted, padding: "8px 0" }}>Loading…</div>
          ) : teamMembers.length === 0 ? (
            <div style={{ fontSize: 13, color: T.textFaint, marginBottom: 16 }}>
              No team members yet. Add someone below.
            </div>
          ) : (
            <div style={{ marginBottom: 16 }}>
              {teamMembers.map(m => (
                <div key={m.id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 0", borderBottom: `0.5px solid ${T.border}`,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden",
                      textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.email}
                      {m.email === currentUserEmail && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: T.textFaint }}>(you)</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: T.textFaint, marginTop: 1 }}>
                      Added {new Date(m.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <select
                    style={{ ...S.select, width: 100, fontSize: 12, padding: "5px 8px" }}
                    value={m.role}
                    disabled={m.email === currentUserEmail}
                    onChange={async e => {
                      if (IS_DEMO) { demoBlockToast(toast); return; }
                      await updateTeamMemberRole(m.id, e.target.value);
                      setTeamMembers(tm => tm.map(x => x.id === m.id ? { ...x, role: e.target.value } : x));
                    }}>
                    <option value="admin">Admin</option>
                    <option value="staff">Staff</option>
                  </select>
                  {isEnterprise && m.role === "staff" && (
                    <StaffLocationPicker vendorUserId={m.id} S={S} T={T} />
                  )}
                  {m.email !== currentUserEmail && (
                    <button
                      style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px",
                        borderColor: "#f87171", color: "#f87171" }}
                      onClick={async () => {
                        if (IS_DEMO) { demoBlockToast(toast); return; }
                        if (!await confirmAction(`Remove ${m.email} from your team?`,
                          { confirmLabel: "Remove" })) return;
                        await removeTeamMember(m.id);
                        setTeamMembers(tm => tm.filter(x => x.id !== m.id));
                      }}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {seatLimitReached ? (
            <div style={{ background: T.surface2, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                {activeSeatCount > 0
                  ? `You're using all ${FREE_TEAM_MEMBERS + activeSeatCount} of your team slots`
                  : "You've used your free team member slot"}
              </div>
              <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 10, lineHeight: 1.6 }}>
                Free includes you plus {FREE_TEAM_MEMBERS} team member. Additional people are $3/month each.
              </div>
              <button
                style={{ ...S.btn("primary"), padding: "10px 16px", fontSize: 13 }}
                onClick={() => { if (IS_DEMO) { demoBlockToast(toast); return; } setShowSeatModal(true); }}>
                {activeSeatCount > 0 ? "Add More People" : "Add Another Person — $3/mo"}
              </button>
            </div>
          ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              style={{ ...S.input, flex: 2, minWidth: 160, fontSize: 13 }}
              type="email"
              placeholder="email@example.com"
              value={newMemberEmail}
              onChange={e => { setNewMemberEmail(e.target.value); setTeamError(""); }}
            />
            <select
              style={{ ...S.select, flex: 1, minWidth: 90, fontSize: 13 }}
              value={newMemberRole}
              onChange={e => setNewMemberRole(e.target.value)}>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
            <button
              style={{ ...S.btn("primary"), padding: "8px 16px", fontSize: 13, flexShrink: 0 }}
              disabled={teamSaving || !newMemberEmail.trim()}
              onClick={async () => {
                if (IS_DEMO) { demoBlockToast(toast); return; }
                const email = newMemberEmail.trim().toLowerCase();
                if (!email.includes("@")) { setTeamError("Enter a valid email."); return; }
                if (teamMembers.some(m => m.email === email)) {
                  setTeamError("This person is already on your team."); return;
                }
                setTeamSaving(true); setTeamError("");
                try {
                  const added = await addTeamMember(vendor.id, email, newMemberRole, currentUserEmail);
                  setTeamMembers(tm => [...tm, added]);
                  setNewMemberEmail("");
                  setNewMemberRole("staff");
                  toast(`${email} added as ${newMemberRole}`, "success");
                  fetch("/invite-team-member", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      email, role: newMemberRole,
                      businessName: vendor.business_name || "your team",
                      invitedBy: currentUserEmail,
                    }),
                  }).catch(()=>{});
                } catch(e) {
                  setTeamError(e.message || "Failed to add team member.");
                } finally {
                  setTeamSaving(false);
                }
              }}>
              {teamSaving ? "Adding…" : "Add"}
            </button>
          </div>
          )}
          {showSeatModal && (
            <PayPalSeatModal
              currentQuantity={activeSeatCount}
              onClose={(result) => {
                setShowSeatModal(false);
                if (result && onVendorUpdate) {
                  onVendorUpdate({ ...vendor, team_seat_quantity: result.quantity, team_seat_status: "active" });
                }
              }}
            />
          )}
          {teamError && (
            <div style={{ fontSize: 12, color: "#f87171", marginTop: 8 }}>{teamError}</div>
          )}
        </div>
      ) : (
        <LockedFeatureCard title="👤 Team Members"
          description="Add staff or additional admins to your account. Each person logs in with their own email, and you can control what Staff can see and do."
          onUpgradeClick={onUpgradeClick}>
          <div style={{ fontSize: 13, color: T.textMuted }}>e.g. brent@vendorstax.com — Staff</div>
        </LockedFeatureCard>
      )}

      {isPaid ? (
        <AuditLogSection vendor={vendor} S={S} T={T} />
      ) : (
        <LockedFeatureCard title="📜 Audit Log"
          description="See a history of who changed what and when, edits, deletions, role changes, and more."
          onUpgradeClick={onUpgradeClick}>
          <div style={{ fontSize: 13, color: T.textMuted }}>e.g. brent@vendorstax.com edited "Charizard ex" · 2h ago</div>
        </LockedFeatureCard>
      )}

      {isAdmin && (
      <div style={{ ...S.card, marginTop: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>📡 Reference Price</div>
        <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6 }}>
          Reference prices are fetched automatically when a card is added to
          inventory. If you just switched to this new pricing source, existing cards will pick up real pricing data
          over the next several nights rather than all at once.
          <br/><br/>
          Need one specific card sooner? Open that card's details and tap 🔄 next to Market Range
          to fetch it right away.
          <br/><br/>
          A card can only be matched if its <strong>Card Name and Card Number</strong> are entered
          correctly. A misspelled name or missing
          number means we can't confidently match it, and it'll be skipped rather than
          risk pricing the wrong card.
          <br/><br/>
          Reference Price is a starting point, not an absolute — {dynamicPricingEnabled
            ? "with Price Setting turned on, your List Price for In Stock cards is updated automatically using the rules below."
            : "your List Price is never changed automatically unless you turn on Price Setting below."}
        </div>
      </div>
      )}

      {hasDynamicPricing ? (
      <div style={{ ...S.card, marginTop: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>💲 Price Setting</div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: dynamicPricingEnabled ? "#4ade80" : T.textMuted }}>
              {dynamicPricingEnabled ? "On" : "Off"}
            </span>
            <div
              onClick={handleTogglePricing}
              style={{
                width: 42, height: 24, borderRadius: 12, cursor: "pointer",
                background: dynamicPricingEnabled ? "#4ade80" : T.surface2,
                border: `0.5px solid ${dynamicPricingEnabled ? "#4ade80" : T.border}`,
                position: "relative", transition: "background 0.15s",
                opacity: dynamicPricingSaving ? 0.5 : 1,
                pointerEvents: dynamicPricingSaving ? "none" : "auto",
              }}>
              <div style={{
                width: 18, height: 18, borderRadius: "50%", background: "#fff",
                position: "absolute", top: 2, left: dynamicPricingEnabled ? 21 : 3,
                transition: "left 0.15s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }} />
            </div>
          </label>
        </div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
          Automatically sets List Price from Reference Price for In Stock cards each time you refresh
          prices above. Cards reserved for an active show are never touched until they're returned to
          inventory. This only turns on for Admin accounts.
        </div>

        <div style={{ ...S.infoBox, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Pricing Rule</div>
          <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 10, lineHeight: 1.6 }}>
            Pick exactly one rule for how List Price gets calculated. Turning one on turns the others off.
          </div>

          {[
            { key: "top", label: "Price all cards at the top of the Price Range" },
            { key: "average", label: "Price all cards at the average of the Price Range" },
            { key: "top_plus_percent", label: "Price all cards at the top of the Price Range plus" },
            { key: "top_plus_dollar", label: "Price all cards at the top of the Price Range plus" },
            { key: "top_minus_percent", label: "Price all cards below the top of the Price Range by" },
            { key: "top_minus_dollar", label: "Price all cards below the top of the Price Range by" },
          ].map(rule => (
            <div key={rule.key} style={{
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
              padding: "8px 0", borderTop: rule.key !== "top" ? `1px solid ${T.border || "rgba(255,255,255,0.08)"}` : "none",
            }}>
              <button
                onClick={() => setDynamicPricingStrategy(rule.key)}
                disabled={saving}
                style={{
                  width: 40, height: 22, borderRadius: 11, border: "none", flexShrink: 0, position: "relative",
                  background: dynamicPricingStrategy === rule.key ? "#3C66EE" : (T.surface2 || "#333"),
                }}>
                <div style={{
                  width: 16, height: 16, borderRadius: 8, background: "#fff", position: "absolute", top: 3,
                  left: dynamicPricingStrategy === rule.key ? 21 : 3, transition: "left 0.15s ease",
                }} />
              </button>
              <span style={{ fontSize: 12, color: T.textMuted, flex: "1 1 auto", minWidth: 160 }}>
                {rule.label}
              </span>
              {(rule.key === "top_plus_percent" || rule.key === "top_minus_percent") && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="number" min="0" max="200" step="1"
                    value={dynamicPricingPercent}
                    onChange={e => setDynamicPricingPercent(e.target.value)}
                    style={{ ...S.input, width: 60, padding: "4px 6px", fontSize: 12, textAlign: "center" }}
                  />
                  <span style={{ fontSize: 12, color: T.textMuted }}>%</span>
                </div>
              )}
              {(rule.key === "top_plus_dollar" || rule.key === "top_minus_dollar") && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 12, color: T.textMuted }}>$</span>
                  <input
                    type="number" min="0" step="1"
                    value={dynamicPricingDollar}
                    onChange={e => setDynamicPricingDollar(e.target.value)}
                    style={{ ...S.input, width: 60, padding: "4px 6px", fontSize: 12, textAlign: "center" }}
                  />
                </div>
              )}
            </div>
          ))}

          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border || "rgba(255,255,255,0.08)"}` }}>
            Never priced below your cost + {((vendor?.markup_rate ?? 1.25) * 100 - 100).toFixed(0)}% margin
          </div>
        </div>

        <div style={{ ...S.infoBox, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Manual Review Ceiling</div>
          <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 8, lineHeight: 1.6 }}>
            Cards above this reference price are flagged for you to price manually, no matter which
            rule above is selected, this always overrides the rule. $100 is a safety net, not
            everyone needs it that low, raise it if you're comfortable letting Price Setting handle
            higher-value cards too.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: T.textMuted }}>Flag cards above $</span>
            <input
              type="number" min="1" step="1"
              value={dynamicPricingCeiling}
              onChange={e => setDynamicPricingCeiling(e.target.value)}
              style={{ ...S.input, width: 80, padding: "4px 6px", fontSize: 12, textAlign: "center" }}
            />
          </div>
        </div>
      </div>
      ) : isPaid ? (
        <div style={{ ...S.card, marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>💲 Price Setting</div>
            <span style={{ fontSize: 10, fontWeight: 700, color: T.primary, background: T.blueBg,
              padding: "2px 8px", borderRadius: T.radius.pill }}>ADD-ON · $9/mo</span>
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 14, lineHeight: 1.6 }}>
            Keep your List Price current automatically as Reference Price moves, no manual repricing
            needed. This is a separate add-on on top of Pro, $5.00/month.
          </div>
          <button style={{ ...S.btn("primary"), padding: "10px 20px" }}
            onClick={() => setShowDynamicPricingModal(true)}>
            Add Dynamic Pricing — $5.00/mo
          </button>
        </div>
      ) : (
        <LockedFeatureCard title="💲 Price Setting" onUpgradeClick={onUpgradeClick}
          description="Keep your List Price current automatically as Reference Price moves, no manual repricing needed.">
          <div style={{ fontSize: 13, color: T.textMuted }}>Price all cards at the top of the Price Range</div>
        </LockedFeatureCard>
      )}

      {showDynamicPricingModal && (
        <PayPalDynamicPricingModal
          onClose={(result) => {
            setShowDynamicPricingModal(false);
            if (result) onVendorUpdate({ ...vendor, dynamic_pricing_addon_status: "active" });
          }}
        />
      )}

      {isPaid ? (
      <div style={{ ...S.card, marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>🤝 Deal Board Contact Info</div>
        <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4, marginBottom: 16, lineHeight: 1.6 }}>
          We keep a phone number on file for every Pro vendor so we can reach you
          directly if something ever needs attention on Deal Board.
        </div>

        <label style={S.label}>Phone Number</label>
        <div style={{ display:"flex", gap:8 }}>
          <input style={{...S.input, flex:1}} value={dealBoardPhone}
            onChange={e=>setDealBoardPhone(e.target.value)} placeholder="(407) 555-0100" />
          <button style={{ ...S.btn("primary"), flexShrink:0 }} disabled={dealBoardSending}
            onClick={async()=>{
              setDealBoardSavingAddress(true);
              try {
                await saveDealBoardPhone(vendor.id, dealBoardPhone);
                onVendorUpdate({ ...vendor, deal_board_phone: dealBoardPhone });
                toast("Phone number saved", "success");
              } catch(e) { toast("Could not save: " + e.message, "error"); }
              setDealBoardSavingAddress(false);
            }}>
            {dealBoardSavingAddress ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      ) : (
        <LockedFeatureCard title="🤝 Deal Board" onUpgradeClick={onUpgradeClick}
          description="Buy, sell, and trade with other vendors directly, available to every Pro member.">
          <div style={{ fontSize:13, color:T.textMuted }}>Included with Pro</div>
        </LockedFeatureCard>
      )}

      {error && (
        <div style={{ ...S.infoBox, borderColor: "#991b1b", marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: "#f87171" }}>{error}</div>
        </div>
      )}

      <button
        style={{ ...S.btn("primary"), width: "100%", padding: "14px", fontSize: 15 }}
        onClick={handleSave}
        disabled={saving}>
        {saving ? "Saving…" : saved ? "✓ Saved" : "Save Settings"}
      </button>

      <div style={{ fontSize: 12, color: T.textFaint, marginTop: 10 }}>
        Changes take effect immediately across all features.
      </div>

      </>)}

      {settingsTab === "cardshop" && !isPaid && (
        <LockedFeatureCard title="🏪 For Card Shops" onUpgradeClick={onUpgradeClick}
          description="Built for shops running Kiosk Mode all the time, not just at shows: display each card's shelf location, hide prices on the kiosk screen, and let customers leave requests for cards you don't have in stock.">
          <div style={{ fontSize:13, color:T.textMuted }}>Store Mode · Display Location · Hide Prices · Request Form</div>
        </LockedFeatureCard>
      )}

      {settingsTab === "cardshop" && isPaid && !isEnterprise && (
        <div style={{ ...S.card, background: "linear-gradient(135deg, rgba(60,102,238,0.12), rgba(124,58,237,0.12))",
          border: "1px solid rgba(60,102,238,0.3)" }}>
          <div style={{ fontSize:15, fontWeight:700, marginBottom:6 }}>🏪 For Card Shops — Enterprise</div>
          <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.6, marginBottom:14 }}>
            Store Mode, company-wide inventory across every location, per-location staff
            permissions, per-category pricing rules, Dynamic Pricing included, and a shared
            CRM with store credit — up to 10 team members before any extra cost.
          </div>
          <a href="mailto:brentparker76@gmail.com?subject=Enterprise%20Plan%20Interest"
            style={{ ...S.btn("primary"), display:"inline-block", textDecoration:"none" }}>
            Contact Us About Enterprise
          </a>
        </div>
      )}

      {settingsTab === "cardshop" && isEnterprise && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          <div style={{ fontSize:12, color:T.textMuted, lineHeight:1.6 }}>
            Features here are built for shops with a physical storefront running Kiosk Mode
            all the time, not just at shows. Everything below defaults to off (or to today's
            behavior) — turning nothing on here changes nothing about how your Kiosk looks.
          </div>

          <LocationsSection vendor={vendor} S={S} T={T} />
          <CategoryPricingSection S={S} T={T} />

          <div style={S.card}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:14, fontWeight:700 }}>Store Mode</div>
                <div style={{ fontSize:12, color:T.textMuted, marginTop:2 }}>
                  A separate, always-on kiosk showing every card currently In Stock, for a
                  physical storefront — independent of whatever show is Active. This runs
                  alongside Show Mode, not instead of it, both can be live at once.
                </div>
              </div>
              <button
                onClick={()=>{ const next=!storeMode; setStoreMode(next); saveShopSetting({ store_mode_enabled: next }); }}
                disabled={shopSaving}
                style={{
                  width:44, height:26, borderRadius:13, border:"none", flexShrink:0, marginLeft:12,
                  background: storeMode ? "#3C66EE" : (T.surface2||"#333"), position:"relative", cursor:"pointer",
                }}>
                <div style={{
                  width:20, height:20, borderRadius:10, background:"#fff", position:"absolute", top:3,
                  left: storeMode ? 21 : 3, transition:"left 0.15s ease",
                }} />
              </button>
            </div>

            {storeMode && storeUrl && (
              <div style={{ marginTop:14, paddingTop:14, borderTop:`0.5px solid ${T.border||"rgba(255,255,255,0.1)"}` }}>
                <label style={S.label}>Your Store URL</label>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <div style={{
                    ...S.input, flex:1, fontSize:11, color:T.textMuted,
                    fontFamily:"'DM Mono',monospace", overflow:"hidden",
                    textOverflow:"ellipsis", whiteSpace:"nowrap",
                    pointerEvents:"none", userSelect:"all",
                  }}>
                    {storeUrl}
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(storeUrl); toast("Store URL copied", "success"); }}
                    style={{ ...S.btn("ghost"), padding:"8px 14px", fontSize:12, flexShrink:0 }}>
                    Copy
                  </button>
                </div>
                <div style={{ fontSize:11, color:T.textFaint, marginTop:6 }}>
                  Open this on a tablet or display device at your storefront.
                </div>
              </div>
            )}
          </div>

          <div style={S.card}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:14, fontWeight:700 }}>Display Location</div>
                <div style={{ fontSize:12, color:T.textMuted, marginTop:2 }}>
                  Show a "Location" field (Display Case, Binder 3, etc.) on each card, both in
                  your app and to shoppers browsing Kiosk Mode.
                </div>
              </div>
              <button
                onClick={()=>{ const next=!showLocation; setShowLocation(next); saveShopSetting({ kiosk_show_location: next }); }}
                disabled={shopSaving}
                style={{
                  width:44, height:26, borderRadius:13, border:"none", flexShrink:0, marginLeft:12,
                  background: showLocation ? "#3C66EE" : (T.surface2||"#333"), position:"relative", cursor:"pointer",
                }}>
                <div style={{
                  width:20, height:20, borderRadius:10, background:"#fff", position:"absolute", top:3,
                  left: showLocation ? 21 : 3, transition:"left 0.15s ease",
                }} />
              </button>
            </div>

            {showLocation && (
              <div style={{ marginTop:14, paddingTop:14, borderTop:`0.5px solid ${T.border||"rgba(255,255,255,0.1)"}` }}>
                <label style={S.label}>Saved Location Values</label>
                <div style={{ fontSize:11, color:T.textFaint, marginBottom:8 }}>
                  New values also get added automatically the first time you type one into a
                  card's Location field — this list is just for reviewing and removing old ones.
                </div>
                {locationOptions.length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:10 }}>
                    {locationOptions.map(val => (
                      <div key={val} style={{
                        display:"flex", alignItems:"center", gap:6, padding:"6px 10px",
                        borderRadius:8, background:T.surface2||"#1a1f2e", fontSize:12,
                      }}>
                        <span>{val}</span>
                        <button onClick={()=>removeLocationOption(val)}
                          style={{ background:"none", border:"none", color:"#f87171", cursor:"pointer", padding:0, fontSize:13 }}>
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display:"flex", gap:8 }}>
                  <input style={{ ...S.input, flex:1 }} placeholder="Add a location value"
                    value={newLocationValue} onChange={e=>setNewLocationValue(e.target.value)}
                    onKeyDown={e=>{ if (e.key==="Enter") addLocationOption(); }} />
                  <button style={{ ...S.btn("primary"), padding:"10px 16px" }} onClick={addLocationOption}>
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={S.card}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:14, fontWeight:700 }}>Hide Prices in Kiosk Mode</div>
                <div style={{ fontSize:12, color:T.textMuted, marginTop:2 }}>
                  Shoppers browsing Kiosk Mode see every card detail except List Price.
                  Useful if you'd rather price in person at the counter.
                </div>
              </div>
              <button
                onClick={()=>{ const next=!hidePrices; setHidePrices(next); saveShopSetting({ kiosk_hide_prices: next }); }}
                disabled={shopSaving}
                style={{
                  width:44, height:26, borderRadius:13, border:"none", flexShrink:0, marginLeft:12,
                  background: hidePrices ? "#3C66EE" : (T.surface2||"#333"), position:"relative", cursor:"pointer",
                }}>
                <div style={{
                  width:20, height:20, borderRadius:10, background:"#fff", position:"absolute", top:3,
                  left: hidePrices ? 21 : 3, transition:"left 0.15s ease",
                }} />
              </button>
            </div>
          </div>

          <div style={S.card}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:14, fontWeight:700 }}>"Can't Find a Card?" Request Form</div>
                <div style={{ fontSize:12, color:T.textMuted, marginTop:2 }}>
                  Lets shoppers in Kiosk Mode leave their info for a card you don't have in stock.
                  Requests always show up under CRM → Requests either way.
                </div>
              </div>
              <button
                onClick={()=>{ const next=!showRequestForm; setShowRequestForm(next); saveShopSetting({ kiosk_show_request_form: next }); }}
                disabled={shopSaving}
                style={{
                  width:44, height:26, borderRadius:13, border:"none", flexShrink:0, marginLeft:12,
                  background: showRequestForm ? "#3C66EE" : (T.surface2||"#333"), position:"relative", cursor:"pointer",
                }}>
                <div style={{
                  width:20, height:20, borderRadius:10, background:"#fff", position:"absolute", top:3,
                  left: showRequestForm ? 21 : 3, transition:"left 0.15s ease",
                }} />
              </button>
            </div>

            <div style={{ marginTop:14, paddingTop:14, borderTop:`0.5px solid ${T.border||"rgba(255,255,255,0.1)"}` }}>
              <label style={S.label}>Email a Copy of New Requests To</label>
              <div style={{ fontSize:11, color:T.textFaint, marginBottom:8 }}>
                Optional — leave blank if you'd rather just check CRM → Requests yourself.
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <input style={{ ...S.input, flex:1 }} type="email" placeholder="you@yourshop.com"
                  value={notifyEmail} onChange={e=>setNotifyEmail(e.target.value)} />
                <button style={{ ...S.btn("primary"), padding:"10px 16px" }}
                  onClick={()=>saveShopSetting({ kiosk_request_notify_email: notifyEmail.trim() || null })}>
                  Save
                </button>
              </div>
            </div>
          </div>

        </div>
      )}

    </div>
  );
}

function LocationsSection({ vendor, S, T }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null); // location being created/edited, or null
  const [saving, setSaving]       = useState(false);
  const { toast } = useNotify ? useNotify() : { toast: () => {} };

  const load = () => {
    setLoading(true);
    readLocations()
      .then(setLocations)
      .catch(e => toast("Couldn't load locations: " + e.message, "error"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const startNew = () => setEditing({ StoreName:"", StoreNumber:"", Address:"", City:"", State:"", Zip:"", Phone:"" });

  const save = async () => {
    if (!editing.StoreName?.trim() || !editing.StoreNumber?.trim()) {
      toast("Store Name and Store Number are both required", "alert");
      return;
    }
    setSaving(true);
    try {
      if (editing.ID) {
        await updateLocation(editing.ID, editing);
      } else {
        await addLocation(editing);
      }
      setEditing(null);
      load();
      toast("Location saved", "success");
    } catch (e) {
      toast("Couldn't save location: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const archive = async (loc) => {
    if (!window.confirm(`Archive ${loc.StoreName}? Its past inventory and sales history stay intact.`)) return;
    try {
      await archiveLocation(loc.ID);
      load();
      toast("Location archived", "success");
    } catch (e) {
      toast("Couldn't archive location: " + e.message, "error");
    }
  };

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>Locations</div>
        {!editing && (
          <button style={S.btn("primary")} onClick={startNew}>+ Add Location</button>
        )}
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, marginBottom: 12 }}>
        Each location gets its own Store Mode URL and can be filtered on the Dashboard and Reporting.
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: T.textMuted }}>Loading…</div>
      ) : editing ? (
        <div>
          <div style={S.row}>
            <div style={S.col}>
              <label style={S.label}>Store Name *</label>
              <input style={S.input} value={editing.StoreName}
                onChange={e=>setEditing({...editing, StoreName:e.target.value})} placeholder="e.g. Downtown" />
            </div>
            <div style={S.col}>
              <label style={S.label}>Store Number *</label>
              <input style={S.input} value={editing.StoreNumber}
                onChange={e=>setEditing({...editing, StoreNumber:e.target.value})} placeholder="e.g. 1" />
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <label style={S.label}>Address</label>
            <input style={S.input} value={editing.Address}
              onChange={e=>setEditing({...editing, Address:e.target.value})} />
          </div>
          <div style={S.row}>
            <div style={S.col}>
              <label style={S.label}>City</label>
              <input style={S.input} value={editing.City}
                onChange={e=>setEditing({...editing, City:e.target.value})} />
            </div>
            <div style={{...S.col, maxWidth:80}}>
              <label style={S.label}>State</label>
              <input style={S.input} value={editing.State}
                onChange={e=>setEditing({...editing, State:e.target.value})} />
            </div>
            <div style={{...S.col, maxWidth:100}}>
              <label style={S.label}>Zip</label>
              <input style={S.input} value={editing.Zip}
                onChange={e=>setEditing({...editing, Zip:e.target.value})} />
            </div>
          </div>
          <div style={{marginBottom:14}}>
            <label style={S.label}>Phone</label>
            <input style={S.input} value={editing.Phone}
              onChange={e=>setEditing({...editing, Phone:e.target.value})} />
          </div>
          <div style={{display:"flex", gap:8}}>
            <button style={S.btn("primary")} onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save Location"}
            </button>
            <button style={S.btn("ghost")} onClick={()=>setEditing(null)} disabled={saving}>Cancel</button>
          </div>
        </div>
      ) : locations.length === 0 ? (
        <div style={{ fontSize: 13, color: T.textMuted }}>No locations yet — add your first one above.</div>
      ) : (
        <div style={{display:"flex", flexDirection:"column", gap:8}}>
          {locations.map(loc => (
            <div key={loc.ID} style={{...S.infoBox, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
              <div>
                <div style={{fontWeight:600, fontSize:14}}>{loc.StoreName} <span style={{color:T.textMuted, fontWeight:400}}>· #{loc.StoreNumber}</span></div>
                {(loc.City || loc.State) && (
                  <div style={{fontSize:12, color:T.textMuted, marginTop:2}}>{[loc.City, loc.State].filter(Boolean).join(", ")}</div>
                )}
              </div>
              <div style={{display:"flex", gap:8}}>
                <button style={{...S.btn("ghost"), padding:"6px 12px", fontSize:12}} onClick={()=>setEditing(loc)}>Edit</button>
                <button style={{...S.btn("ghost"), padding:"6px 12px", fontSize:12, color:"#f87171"}} onClick={()=>archive(loc)}>Archive</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AuditLogSection({ vendor, S, T }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { toast } = useNotify ? useNotify() : { toast: () => {} };

  const ACTION_LABELS = {
    "card.added":         "Added card",
    "card.edited":        "Edited card",
    "card.deleted":       "Deleted card",
    "card.sold":          "Sold card",
    "card.price_changed": "Changed price",
    "show.opened":        "Opened show",
    "show.closed":        "Closed show",
    "show.archived":      "Archived show",
    "import.completed":   "Imported cards",
    "settings.changed":   "Changed settings",
    "user.added":         "Added team member",
    "user.removed":       "Removed team member",
  };

  const load = async () => {
    if (!vendor?.id) return;
    setLoading(true);
    try {
      const data = await readAuditLog(vendor.id, 100);
      setLogs(data);
    } catch(e) {
      console.error("Audit log:", e);
    } finally {
      setLoading(false);
    }
  };

  const timeAgo = (ts) => {
    const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s/60)}m ago`;
    if (s < 86400) return `${Math.floor(s/3600)}h ago`;
    return new Date(ts).toLocaleDateString();
  };

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>📋 Activity Log</div>
        <button style={{ ...S.btn("ghost"), fontSize: 12, padding:"4px 10px" }}
          onClick={() => { setExpanded(e => !e); if (!expanded) load(); }}>
          {expanded ? "Hide" : "Show"}
        </button>
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: expanded ? 14 : 0, lineHeight: 1.5 }}>
        Admin-only view of recent changes. Records purge after 90 days.
      </div>

      {expanded && (
        <>
          {loading ? (
            <div style={{ fontSize: 13, color: T.textMuted, padding: "12px 0" }}>Loading…</div>
          ) : logs.length === 0 ? (
            <div style={{ fontSize: 13, color: T.textFaint, padding: "8px 0" }}>No activity recorded yet.</div>
          ) : (
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {logs.map(log => (
                <div key={log.id} style={{
                  display:"flex", gap:10, padding:"8px 0",
                  borderBottom:`0.5px solid ${T.border}`,
                }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:500 }}>
                      {ACTION_LABELS[log.action] || log.action}
                      {log.record_name && (
                        <span style={{ color: T.textMuted, fontWeight:400 }}> — {log.record_name}</span>
                      )}
                    </div>
                    <div style={{ fontSize:11, color: T.textFaint, marginTop:2 }}>
                      {log.user_email}
                      {log.old_value?.list_price && log.new_value?.list_price && (
                        <span> · ${parseFloat(log.old_value.list_price).toFixed(2)} → ${parseFloat(log.new_value.list_price).toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize:11, color: T.textFaint, flexShrink:0, textAlign:"right" }}>
                    {timeAgo(log.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
          <button style={{ ...S.btn("ghost"), width:"100%", marginTop:10, fontSize:12 }}
            onClick={load}>
            ↻ Refresh
          </button>
        </>
      )}
    </div>
  );
}

function PayPalDynamicPricingModal({ onClose }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();
  const [sdkReady, setSdkReady] = useState(false);
  const [linking, setLinking] = useState(false);
  const buttonsRef = useRef(null);
  const renderedButtons = useRef(null);

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const planId = process.env.REACT_APP_PAYPAL_PLAN_DYNAMIC_PRICING;

  useEffect(() => {
    if (!clientId) return;
    if (window.paypal) { setSdkReady(true); return; }
    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription`;
    script.onload = () => setSdkReady(true);
    script.onerror = () => toast("Couldn't load PayPal — please try again", "error");
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, [clientId]);

  useEffect(() => {
    if (!sdkReady || !window.paypal || !buttonsRef.current || !planId) return;
    if (renderedButtons.current) { renderedButtons.current.close(); renderedButtons.current = null; }
    buttonsRef.current.innerHTML = "";

    renderedButtons.current = window.paypal.Buttons({
      style: { shape: "pill", color: "blue", layout: "vertical", label: "subscribe" },
      createSubscription: (data, actions) => actions.subscription.create({ plan_id: planId }),
      onApprove: async (data) => {
        setLinking(true);
        try {
          await linkDynamicPricingSubscription(data.subscriptionID);
          toast("Dynamic Pricing is on!", "success");
          onClose(true);
        } catch(e) {
          toast("Payment approved, but we couldn't finish linking it: " + e.message, "error");
          setLinking(false);
        }
      },
      onError: () => toast("Something went wrong with PayPal — please try again", "error"),
    });
    renderedButtons.current.render(buttonsRef.current);
  }, [sdkReady, planId]);

  return (
    <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) onClose(null); }}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div style={S.h2}>Add Dynamic Pricing</div>
          <button style={{...S.btn("ghost"),padding:"4px 10px"}} onClick={()=>onClose(null)}>✕</button>
        </div>
        <div style={{fontSize:13, color:T.textMuted, marginBottom:20, lineHeight:1.6}}>
          $5.00/month, on top of Pro. Keeps your List Price current automatically as Reference
          Price moves, no manual repricing.
        </div>

        {linking ? (
          <div style={{textAlign:"center", padding:"20px 0", fontSize:13, color:T.textMuted}}>
            Finishing up…
          </div>
        ) : !clientId || !planId ? (
          <div style={{fontSize:13, color:T.textFaint}}>
            Dynamic Pricing purchases aren't configured yet — contact support.
          </div>
        ) : (
          <div ref={buttonsRef} />
        )}
      </div>
    </div>
  );
}

function PayPalSeatModal({ currentQuantity = 0, onClose }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();
  const [quantity, setQuantity] = useState(Math.max(currentQuantity, 1));
  const [sdkReady, setSdkReady] = useState(false);
  const [linking, setLinking] = useState(false);
  const buttonsRef = useRef(null);
  const renderedButtons = useRef(null);

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const planId = process.env.REACT_APP_PAYPAL_PLAN_TEAM_SEAT;

  useEffect(() => {
    if (!clientId) return;
    if (window.paypal) { setSdkReady(true); return; }
    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription`;
    script.onload = () => setSdkReady(true);
    script.onerror = () => toast("Couldn't load PayPal — please try again", "error");
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, [clientId]);

  useEffect(() => {
    if (!sdkReady || !window.paypal || !buttonsRef.current || !planId) return;
    if (renderedButtons.current) {
      renderedButtons.current.close();
      renderedButtons.current = null;
    }
    buttonsRef.current.innerHTML = "";

    renderedButtons.current = window.paypal.Buttons({
      style: { shape: "pill", color: "blue", layout: "vertical", label: "subscribe" },
      createSubscription: (data, actions) => actions.subscription.create({
        plan_id: planId,
        quantity: String(quantity),
      }),
      onApprove: async (data) => {
        setLinking(true);
        try {
          const result = await linkSeatSubscription(data.subscriptionID);
          toast(`${result.quantity} additional ${result.quantity === 1 ? "person" : "people"} added`, "success");
          onClose({ quantity: result.quantity });
        } catch(e) {
          toast("Payment approved, but we couldn't finish linking it: " + e.message, "error");
          setLinking(false);
        }
      },
      onError: () => {
        toast("Something went wrong with PayPal — please try again", "error");
      },
    });
    renderedButtons.current.render(buttonsRef.current);
  }, [sdkReady, planId, quantity]);

  return (
    <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) onClose(null); }}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div style={S.h2}>Add Team Members</div>
          <button style={{...S.btn("ghost"),padding:"4px 10px"}} onClick={()=>onClose(null)}>✕</button>
        </div>
        <div style={{fontSize:13, color:T.textMuted, marginBottom:16, lineHeight:1.6}}>
          $3/month per additional person, on top of your free slot. Choose how many total you need,
          this replaces any previous seat purchase, not adds to it.
        </div>

        <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:20}}>
          <label style={{fontSize:13, fontWeight:600}}>Additional people:</label>
          <div style={{display:"flex", alignItems:"center", gap:8}}>
            <button
              style={{...S.btn("ghost"), width:32, height:32, padding:0, fontSize:16}}
              disabled={quantity <= 1}
              onClick={()=>setQuantity(q => Math.max(1, q - 1))}>
              −
            </button>
            <div style={{width:28, textAlign:"center", fontSize:15, fontWeight:700}}>{quantity}</div>
            <button
              style={{...S.btn("ghost"), width:32, height:32, padding:0, fontSize:16}}
              onClick={()=>setQuantity(q => q + 1)}>
              +
            </button>
          </div>
          <div style={{fontSize:13, color:T.textMuted, marginLeft:"auto"}}>
            ${quantity * 3}/month
          </div>
        </div>

        {linking ? (
          <div style={{textAlign:"center", padding:"20px 0", fontSize:13, color:T.textMuted}}>
            Finishing up…
          </div>
        ) : !clientId || !planId ? (
          <div style={{fontSize:13, color:T.textFaint}}>
            Seat purchases aren't configured yet — contact support.
          </div>
        ) : (
          <div ref={buttonsRef} />
        )}
      </div>
    </div>
  );
}
